/**
 * Affiliates → Assets: the swipe library. Ready-made emails, posts, images,
 * videos and links our affiliates copy when they share SyncSuite. "Write it
 * for me" drafts one from a short brief (AI lite, on click; a plain template
 * when it's offline). [your link] marks where their referral link goes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Film, Image as ImageIcon, Link2, Mail, Pencil, Plus, Trash2, WandSparkles } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, FieldHint, Input, Label, Modal, ModalFooter, Select, Skeleton, Textarea, useConfirm, useToast } from "@/components/ui";
import { Card, Empty, Pill, Segmented, timeAgo } from "@/features/command/ui";
import { CopyButton } from "./shared";
import type { AffiliateAsset, AssetKind } from "./types";

export const ASSET_KINDS: AssetKind[] = ["post", "email", "image", "video", "link"];
const KIND_META: Record<AssetKind, { label: string; plural: string; icon: typeof Mail; color: string }> = {
  post: { label: "Post", plural: "Posts", icon: FileText, color: "indigo" },
  email: { label: "Email", plural: "Emails", icon: Mail, color: "sky" },
  image: { label: "Image", plural: "Images", icon: ImageIcon, color: "pink" },
  video: { label: "Video", plural: "Videos", icon: Film, color: "violet" },
  link: { label: "Link", plural: "Links", icon: Link2, color: "teal" },
};

const isImage = (url: string | null) => !!url && /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i.test(url);

export function AssetsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [assets, setAssets] = useState<AffiliateAsset[] | null>(null);
  const [filter, setFilter] = useState<AssetKind | "all">("all");
  const [editing, setEditing] = useState<AffiliateAsset | "new" | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ assets: AffiliateAsset[] }>("/affiliates/assets");
      setAssets(d.assets);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load the library");
      setAssets((a) => a ?? []);
    }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => (assets ?? []).filter((a) => filter === "all" || a.kind === filter), [assets, filter]);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of assets ?? []) c[a.kind] = (c[a.kind] ?? 0) + 1;
    return c;
  }, [assets]);

  const remove = async (a: AffiliateAsset) => {
    if (!(await confirm({ title: `Delete "${a.title}"?`, description: "It's removed from the library. Anyone who already copied it keeps their copy.", confirmLabel: "Delete", danger: true }))) return;
    try {
      await api.delete(`/affiliates/assets/${a.id}`);
      setAssets((list) => (list ?? []).filter((x) => x.id !== a.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete it");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-zinc-400">Ready-made posts, emails and media our affiliates copy. Write <span className="font-mono text-[13px] text-zinc-300">[your link]</span> where their link goes.</p>
        <Button className="shrink-0" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Add to the library</Button>
      </div>

      {assets && assets.length > 0 && (
        <div className="-mx-1 overflow-x-auto whitespace-nowrap px-1 pb-0.5">
          <Segmented<AssetKind | "all">
            size="sm"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: `All ${assets.length}` },
              ...ASSET_KINDS.filter((k) => counts[k]).map((k) => ({ value: k, label: `${KIND_META[k].plural} ${counts[k]}` })),
            ]}
          />
        </div>
      )}

      {assets == null ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}</div>
      ) : assets.length === 0 ? (
        <Card>
          <Empty
            icon={WandSparkles}
            title="The library is empty"
            action={<Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setEditing("new")}>Add the first one</Button>}
          >
            Affiliates share more when there's something fresh to post. Add a post, an email or a video script, or have one written for you from a sentence.
          </Empty>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((a) => {
            const meta = KIND_META[a.kind] ?? KIND_META.post;
            const Icon = meta.icon;
            return (
              <Card key={a.id} className="flex min-w-0 flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Pill color={meta.color}><Icon className="h-3 w-3" />{meta.label}</Pill>
                    <h3 className="mt-1.5 text-sm font-semibold leading-snug text-zinc-100">{a.title}</h3>
                  </div>
                  <div className="flex shrink-0 items-center">
                    <button type="button" onClick={() => setEditing(a)} title="Edit" aria-label="Edit" className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800/70 hover:text-zinc-100"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => void remove(a)} title="Delete" aria-label="Delete" className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800/70 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                {isImage(a.media_url) && (
                  <img src={a.media_url!} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="mt-3 max-h-40 w-full rounded-lg bg-zinc-900 object-cover" />
                )}
                {a.body && <p className="mt-2 line-clamp-6 whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-400">{a.body}</p>}
                {a.media_url && !isImage(a.media_url) && (
                  <a href={a.media_url} target="_blank" rel="noopener noreferrer" className="mt-2 block truncate text-xs text-indigo-300 hover:text-indigo-200">{a.media_url}</a>
                )}
                {a.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">{a.tags.map((t) => <span key={t} className="rounded-full bg-zinc-800/70 px-2 py-0.5 text-[11px] text-zinc-400">#{t}</span>)}</div>
                )}
                <div className="mt-auto flex flex-wrap items-center gap-1 pt-3">
                  {a.body && <CopyButton text={a.body} label="Copy text" />}
                  {a.media_url && <CopyButton text={a.media_url} label="Copy link" />}
                  <span className="ml-auto text-[11px] text-zinc-500">{timeAgo(a.updated_at)}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AssetModal
        asset={editing}
        onClose={() => setEditing(null)}
        onSaved={(a, isNew) => {
          setEditing(null);
          setAssets((list) => (isNew ? [a, ...(list ?? [])] : (list ?? []).map((x) => (x.id === a.id ? a : x))));
          toast.success(isNew ? "Added to the library" : "Saved");
        }}
      />
    </div>
  );
}

function AssetModal({ asset, onClose, onSaved }: { asset: AffiliateAsset | "new" | null; onClose: () => void; onSaved: (a: AffiliateAsset, isNew: boolean) => void }) {
  const toast = useToast();
  const isNew = asset === "new";
  const [kind, setKind] = useState<AssetKind>("post");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [media, setMedia] = useState("");
  const [tags, setTags] = useState("");
  const [brief, setBrief] = useState("");
  const [writing, setWriting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!asset) return;
    const a = asset === "new" ? null : asset;
    setKind(a?.kind ?? "post"); setTitle(a?.title ?? ""); setBody(a?.body ?? ""); setMedia(a?.media_url ?? "");
    setTags((a?.tags ?? []).join(", ")); setBrief(""); setTried(false); setNote(null);
  }, [asset]);

  const write = async () => {
    setWriting(true);
    try {
      const d = await api.post<{ title: string; body: string; via: "ai" | "rules" }>("/affiliates/assets/write", { kind, brief });
      if (!title.trim() || isNew) setTitle(d.title);
      setBody(d.body);
      setNote(d.via === "ai" ? "Written by AI. Read it over before you save." : "The AI couldn't be reached, so this is a plain template. Make it yours.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't write it");
    } finally { setWriting(false); }
  };

  const mediaBad = media.trim() !== "" && !/^https?:\/\/\S+\.\S+$/i.test(media.trim());
  const save = async () => {
    setTried(true);
    if (!title.trim() || mediaBad) return;
    setSaving(true);
    const payload = { kind, title: title.trim(), body: body.trim() || null, media_url: media.trim() || null, tags: tags.split(/[,\n]/).map((t) => t.trim()).filter(Boolean) };
    try {
      const d = isNew
        ? await api.post<{ asset: AffiliateAsset }>("/affiliates/assets", payload)
        : await api.patch<{ asset: AffiliateAsset }>(`/affiliates/assets/${(asset as AffiliateAsset).id}`, payload);
      onSaved(d.asset, isNew);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save it");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={!!asset} onClose={onClose} size="lg" title={isNew ? "Add to the library" : "Edit"} description="Something an affiliate can copy and share as their own.">
      <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="-mx-5 max-h-[calc(100dvh-13rem)] space-y-4 overflow-y-auto px-5 pb-1">
          <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <div>
              <Label htmlFor="asset-kind">Kind</Label>
              <Select id="asset-kind" value={kind} onChange={(e) => setKind(e.target.value as AssetKind)}>
                {ASSET_KINDS.map((k) => <option key={k} value={k}>{KIND_META[k].label}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="asset-title" required>Title</Label>
              <Input id="asset-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Post: how I built my booking page" invalid={tried && !title.trim()} />
            </div>
          </div>

          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-3">
            <Label htmlFor="asset-brief" className="mb-1">Have it written for you</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="min-w-0 flex-1">
                <Input id="asset-brief" value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="What should it say? e.g. built my booking page in 10 minutes" />
              </div>
              <Button type="button" variant="secondary" loading={writing} leftIcon={<WandSparkles className="h-3.5 w-3.5" />} onClick={() => void write()} className="shrink-0">
                Write it for me
              </Button>
            </div>
            {note && <p className="mt-1.5 text-[11px] text-zinc-500">{note}</p>}
          </div>

          <div>
            <Label htmlFor="asset-body">{kind === "email" ? "Email (start with Subject: on the first line)" : kind === "video" ? "Script or caption" : "Text"}</Label>
            <Textarea id="asset-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="text-[13px] leading-relaxed" placeholder="Write [your link] where their referral link goes." />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="asset-media">{kind === "image" ? "Image link" : kind === "video" ? "Video link" : "Link (optional)"}</Label>
              <Input id="asset-media" value={media} onChange={(e) => setMedia(e.target.value)} placeholder="https://..." invalid={tried && mediaBad} />
              {tried && mediaBad && <FieldHint error>It should start with https://</FieldHint>}
            </div>
            <div>
              <Label htmlFor="asset-tags">Tags</Label>
              <Input id="asset-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="booking, launch" />
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={saving}>{isNew ? "Add" : "Save"}</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
